import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Package, Edit2, Trash2, Eye, Loader2, Plus } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const categoryStyles = {
  sale: { label: 'For Sale', className: 'bg-[#F4F1EA] text-[#2C362F] border-0' },
  free: { label: 'Free', className: 'bg-[#E5B66A]/10 text-[#2C362F] border-0' },
  lending: { label: 'Lending', className: 'bg-[#7D9B8B]/10 text-[#7D9B8B] border-0' },
};

export default function MyListingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/listings/my').then(r => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await api.delete(`/listings/${listingId}`);
      setListings(prev => prev.filter(l => l.listing_id !== listingId));
      toast.success('Listing deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen" data-testid="my-listings-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2C362F]" style={{ fontFamily: 'Outfit, sans-serif' }}>My Listings</h1>
          <Link to="/create-listing">
            <Button className="rounded-full bg-primary text-white gap-1.5" data-testid="create-new-listing-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} />New Listing
            </Button>
          </Link>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E8E6DF] rounded-2xl">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-lg font-medium text-[#2C362F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No listings yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create your first listing to start selling</p>
            <Link to="/create-listing">
              <Button className="rounded-full bg-primary text-white">Post a Listing</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map(listing => {
              const cat = categoryStyles[listing.category] || categoryStyles.sale;
              const imgUrl = listing.images?.[0]
                ? `${API_BASE}/api/files/${listing.images[0]}`
                : 'https://images.unsplash.com/photo-1578653282816-a508bd2d0435?w=200&h=150&fit=crop';
              return (
                <div key={listing.listing_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-4 flex items-center gap-4" data-testid={`my-listing-${listing.listing_id}`}>
                  <div className="w-20 h-16 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                    <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#2C362F] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{listing.title}</h3>
                      <Badge className={`rounded-full text-[10px] px-2 py-0.5 ${cat.className}`}>{cat.label}</Badge>
                      {listing.status !== 'active' && (
                        <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0.5 text-destructive">{listing.status}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {listing.category === 'sale' && listing.price != null && <span className="font-medium text-primary">£{listing.price.toFixed(2)}</span>}
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{listing.views} views</span>
                      <span>{new Date(listing.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate(`/listings/${listing.listing_id}`)} data-testid={`view-listing-${listing.listing_id}`}>
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-xl text-destructive" onClick={() => handleDelete(listing.listing_id)} data-testid={`delete-listing-${listing.listing_id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
